import { create } from 'zustand';
import { api } from '../services/apiService';
import type {
  LogDate, LogContent, DocsStats,
  BrowseRoot, TreeNode, FileContent, DirectoryListing, FileSearchResult, RecentFile,
} from '../types/docs';

type ViewMode = 'today' | 'tree' | 'legacy';

interface DocsState {
  // View mode
  viewMode: ViewMode;

  // File browser state
  roots: BrowseRoot[];
  activeRoot: string;
  tree: TreeNode[];
  selectedPath: string | null;
  fileContent: FileContent | null;

  // Editor state
  editMode: boolean;
  editBuffer: string;
  isDirty: boolean;
  saving: boolean;

  // Recent files state (today view)
  recentFiles: RecentFile[];
  recentLoading: boolean;

  // Search state
  searchQuery: string;
  searchResults: FileSearchResult[];
  searching: boolean;

  // Loading
  treeLoading: boolean;
  contentLoading: boolean;

  // Legacy state
  dates: LogDate[];
  selectedDate: string | null;
  legacyContent: LogContent | null;
  stats: DocsStats | null;
  loading: boolean;

  // View actions
  setViewMode: (mode: ViewMode) => void;
  setActiveRoot: (rootId: string) => void;

  // Recent files actions
  fetchRecentFiles: () => Promise<void>;

  // File browser actions
  fetchRoots: () => Promise<void>;
  fetchTree: (rootId?: string) => Promise<void>;
  expandDirectory: (dirPath: string) => Promise<void>;
  collapseDirectory: (dirPath: string) => void;
  selectFile: (filePath: string) => Promise<void>;
  navigateToPath: (filePath: string) => Promise<void>;

  // Editor actions
  setEditMode: (on: boolean) => void;
  updateEditBuffer: (content: string) => void;
  saveFile: () => Promise<boolean>;

  // Search actions
  searchFiles: (query: string) => Promise<void>;
  clearSearch: () => void;

  // Legacy actions
  fetchDates: () => Promise<void>;
  fetchContent: (date: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  setSelectedDate: (date: string) => void;

}

/**
 * Recursively update a tree node's children and expansion state.
 */
function updateTreeNode(nodes: TreeNode[], targetPath: string, updater: (node: TreeNode) => TreeNode): TreeNode[] {
  return nodes.map(node => {
    if (node.path === targetPath) {
      return updater(node);
    }
    if (node.children) {
      return { ...node, children: updateTreeNode(node.children, targetPath, updater) };
    }
    return node;
  });
}

/**
 * Expand parent directories to reveal a file in the tree.
 * Returns the list of directory paths that need to be expanded.
 */
function getParentPaths(filePath: string, rootPath: string): string[] {
  const relative = filePath.replace(rootPath, '');
  const segments = relative.split('/').filter(Boolean);
  const paths: string[] = [];
  let current = rootPath;
  // All segments except the last (which is the file)
  for (let i = 0; i < segments.length - 1; i++) {
    current += segments[i] + '/';
    paths.push(current);
  }
  return paths;
}

export const useDocsStore = create<DocsState>((set, get) => ({
  // Initial state
  viewMode: 'today',
  roots: [],
  activeRoot: 'union-threads',
  tree: [],
  selectedPath: null,
  fileContent: null,
  editMode: false,
  editBuffer: '',
  isDirty: false,
  saving: false,
  recentFiles: [],
  recentLoading: false,
  searchQuery: '',
  searchResults: [],
  searching: false,
  treeLoading: false,
  contentLoading: false,
  dates: [],
  selectedDate: null,
  legacyContent: null,
  stats: null,
  loading: false,

  // ─── View actions ────────────────────────────────────────────────

  setViewMode: (mode) => set({ viewMode: mode }),

  setActiveRoot: (rootId) => {
    set({ activeRoot: rootId, tree: [], selectedPath: null, fileContent: null, editMode: false });
    get().fetchTree(rootId);
  },

  // ─── Recent files actions ─────────────────────────────────────────

  fetchRecentFiles: async () => {
    set({ recentLoading: true });
    try {
      const { activeRoot } = get();
      const files = await api.get<RecentFile[]>(`/docs/recent?root=${activeRoot}`);
      set({ recentFiles: files, recentLoading: false });
    } catch (err) {
      console.error('Failed to fetch recent files:', err);
      set({ recentLoading: false });
    }
  },

  // ─── File browser actions ────────────────────────────────────────

  fetchRoots: async () => {
    try {
      const roots = await api.get<BrowseRoot[]>('/docs/roots');
      set({ roots });
    } catch (err) {
      console.error('Failed to fetch roots:', err);
    }
  },

  fetchTree: async (rootId?: string) => {
    const root = rootId || get().activeRoot;
    set({ treeLoading: true });
    try {
      const tree = await api.get<TreeNode[]>(`/docs/tree?root=${root}&depth=1`);
      // Mark all as not loaded/expanded
      const enriched = tree.map(node => ({ ...node, loaded: false, expanded: false }));
      set({ tree: enriched, treeLoading: false });
    } catch (err) {
      console.error('Failed to fetch tree:', err);
      set({ treeLoading: false });
    }
  },

  expandDirectory: async (dirPath: string) => {
    const { tree } = get();

    // Find the node - if already loaded, just toggle expanded
    const findNode = (nodes: TreeNode[]): TreeNode | undefined => {
      for (const n of nodes) {
        if (n.path === dirPath) return n;
        if (n.children) {
          const found = findNode(n.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    const node = findNode(tree);
    if (node?.loaded) {
      set({ tree: updateTreeNode(tree, dirPath, n => ({ ...n, expanded: !n.expanded })) });
      return;
    }

    // Mark as loading (expanded but not yet loaded)
    set({ tree: updateTreeNode(tree, dirPath, n => ({ ...n, expanded: true })) });

    try {
      const listing = await api.get<DirectoryListing>(`/docs/ls?path=${encodeURIComponent(dirPath)}`);
      const children = listing.entries.map(entry => ({
        ...entry,
        loaded: false,
        expanded: false,
      }));

      set(state => ({
        tree: updateTreeNode(state.tree, dirPath, n => ({
          ...n,
          children,
          loaded: true,
          expanded: true,
        })),
      }));
    } catch (err) {
      console.error('Failed to expand directory:', err);
      set(state => ({
        tree: updateTreeNode(state.tree, dirPath, n => ({ ...n, expanded: false })),
      }));
    }
  },

  collapseDirectory: (dirPath: string) => {
    set(state => ({
      tree: updateTreeNode(state.tree, dirPath, n => ({ ...n, expanded: false })),
    }));
  },

  selectFile: async (filePath: string) => {
    // Check for unsaved changes
    const { isDirty } = get();
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmed) return;
    }

    set({ selectedPath: filePath, contentLoading: true, editMode: false, isDirty: false });

    try {
      const content = await api.get<FileContent>(`/docs/file?path=${encodeURIComponent(filePath)}`);
      set({ fileContent: content, editBuffer: content.content, contentLoading: false });
    } catch (err) {
      console.error('Failed to read file:', err);
      set({ fileContent: null, contentLoading: false });
    }
  },

  navigateToPath: async (filePath: string) => {
    const { roots, activeRoot, fetchTree, expandDirectory, selectFile } = get();

    // Determine which root this file belongs to
    const root = roots.find(r => filePath.startsWith(r.path));
    if (root && root.id !== activeRoot) {
      set({ activeRoot: root.id });
      await fetchTree(root.id);
    }

    // Expand parent directories
    const rootPath = root?.path || '/opt/union-threads/';
    const parentPaths = getParentPaths(filePath, rootPath);

    for (const parentPath of parentPaths) {
      await expandDirectory(parentPath);
    }

    // Select the file
    await selectFile(filePath);
  },

  // ─── Editor actions ──────────────────────────────────────────────

  setEditMode: (on) => {
    const { fileContent } = get();
    if (on && fileContent) {
      set({ editMode: true, editBuffer: fileContent.content, isDirty: false });
    } else {
      set({ editMode: false, isDirty: false });
    }
  },

  updateEditBuffer: (content) => {
    const { fileContent } = get();
    set({
      editBuffer: content,
      isDirty: content !== fileContent?.content,
    });
  },

  saveFile: async () => {
    const { selectedPath, editBuffer } = get();
    if (!selectedPath) return false;

    set({ saving: true });
    try {
      await api.put<{ success: boolean }>('/docs/file', { path: selectedPath, content: editBuffer });
      // Update the stored content to match
      set(state => ({
        saving: false,
        isDirty: false,
        fileContent: state.fileContent ? { ...state.fileContent, content: editBuffer, size: editBuffer.length } : null,
      }));
      return true;
    } catch (err) {
      console.error('Failed to save file:', err);
      set({ saving: false });
      return false;
    }
  },

  // ─── Search actions ──────────────────────────────────────────────

  searchFiles: async (query: string) => {
    set({ searchQuery: query });
    if (query.length < 2) {
      set({ searchResults: [], searching: false });
      return;
    }

    set({ searching: true });
    try {
      const { activeRoot } = get();
      const results = await api.get<FileSearchResult[]>(
        `/docs/search?q=${encodeURIComponent(query)}&root=${activeRoot}`,
      );
      set({ searchResults: results, searching: false });
    } catch (err) {
      console.error('Failed to search files:', err);
      set({ searching: false });
    }
  },

  clearSearch: () => {
    set({ searchQuery: '', searchResults: [], searching: false });
  },

  // ─── Legacy actions ──────────────────────────────────────────────

  fetchDates: async () => {
    set({ loading: true });
    try {
      const dates = await api.get<LogDate[]>('/docs/dates');
      set({ dates, loading: false });
    } catch (err) {
      console.error('Failed to fetch docs dates:', err);
      set({ loading: false });
    }
  },

  fetchContent: async (date: string) => {
    set({ contentLoading: true });
    try {
      const content = await api.get<LogContent>(`/docs/${date}`);
      set({ legacyContent: content, contentLoading: false });
    } catch (err) {
      console.error('Failed to fetch doc content:', err);
      set({ legacyContent: null, contentLoading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.get<DocsStats>('/docs/stats');
      set({ stats });
    } catch (err) {
      console.error('Failed to fetch docs stats:', err);
    }
  },

  setSelectedDate: (date: string) => {
    set({ selectedDate: date });
    get().fetchContent(date);
  },

}));
